<script>
	import { formatSeconds, parseLocalTimeMs, formatTime, STAGE } from './DataParser.js';

	export let ourData = null;
	export let samsungData = null;

	function calcSamsungStageDurations(stages) {
		if (!stages || stages.length === 0) return null;
		const totals = { DEEP: 0, LIGHT: 0, REM: 0, AWAKE: 0 };
		for (const s of stages) {
			const dur = (s.endTime - s.startTime) / 1000;
			if (totals[s.stage] !== undefined) {
				totals[s.stage] += dur;
			}
		}
		return { deep: totals.DEEP, light: totals.LIGHT, rem: totals.REM, awake: totals.AWAKE };
	}

	$: samsungDurations = samsungData ? calcSamsungStageDurations(samsungData.stages) : null;

	// Sleepwave 단계별 시간 (비율 × 총 수면시간)
	$: ourDurations = (() => {
		const sd = ourData?.sleepData;
		if (!sd?.totalSleepTime) return null;
		const total = sd.totalSleepTime;
		return {
			deep: (sd.deepSleepRate || 0) / 100 * total,
			light: (sd.lightSleepRate || 0) / 100 * total,
			rem: (sd.remSleepRate || 0) / 100 * total,
			awake: sd.wasoTime || 0,
		};
	})();

	// 입면시각: Sleepwave - Light(2) 수면 최초 등장 시점
	$: sleepwaveOnsetTime = (() => {
		if (!ourData?.rawData?.rawSleepStage || !ourData?.sleepData?.timeStart) return null;
		const raw = ourData.rawData.rawSleepStage;
		const startMs = parseLocalTimeMs(ourData.sleepData.timeStart);
		for (let i = 0; i < raw.length; i++) {
			if (parseInt(raw[i]) === STAGE.LIGHT) {
				return startMs + i * 60000;
			}
		}
		return null;
	})();

	// 입면시각: Samsung - 세션 시작 시점 (sleep.startTime 우선, 없으면 stages 최솟값)
	// sleep.startTime은 UTC epoch ms → formatTime()이 브라우저 로컬(KST)로 자동 변환
	$: samsungOnsetTime = (() => {
		if (samsungData?.sleep?.startTime) {
			return samsungData.sleep.startTime; // UTC epoch ms, formatTime에서 KST 변환
		}
		if (!samsungData?.stages || samsungData.stages.length === 0) return null;
		return Math.min(...samsungData.stages.map(s => s.startTime));
	})();

	// Samsung SL (초): 케어 시작 → Samsung 세션 시작
	// careStartMs: parseLocalTimeMs → KST 로컬 시각을 UTC epoch ms로 반환
	// samsungData.sleep.startTime: DB UTC epoch ms
	// 둘 다 UTC epoch ms 기준이므로 직접 차이 계산
	$: samsungSLSec = (() => {
		if (!samsungData?.sleep?.startTime || !ourData?.sleepData?.timeStart) return null;
		const careStartMs = parseLocalTimeMs(ourData.sleepData.timeStart);
		return (samsungData.sleep.startTime - careStartMs) / 1000;
	})();

	// Samsung SE (%): TST / TIB × 100
	$: samsungSECalc = (() => {
		if (samsungSLSec === null || samsungSLSec < 0) return null; // Samsung이 케어 전 시작
		if (!samsungDurations) return null;
		const tst = samsungDurations.deep + samsungDurations.light + samsungDurations.rem;
		const spt = tst + samsungDurations.awake;
		const tib = spt + samsungSLSec;
		return tib > 0 ? tst / tib * 100 : 0;
	})();
</script>

<div class="card mb-4">
	<div class="card-header">
		<h5 class="card-title mb-0">요약 비교</h5>
	</div>
	<div class="card-body">
		<div class="table-responsive">
			<table class="table table-bordered table-sm mb-0">
				<thead class="table-light">
					<tr>
						<th style="width:30%">지표</th>
						<th style="width:35%">Sleepwave</th>
						<th style="width:35%">Samsung Health</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td class="fw-medium">총 수면 시간</td>
						<td>{ourData ? formatSeconds(ourData.sleepData?.totalSleepTime) : '-'}</td>
						<td>{samsungDurations ? formatSeconds(samsungDurations.deep + samsungDurations.light + samsungDurations.rem) : '-'}</td>
					</tr>
					<tr>
						<td class="fw-medium">수면 효율</td>
						<td>{ourData?.sleepData?.sleepEfficiency ? ourData.sleepData.sleepEfficiency.toFixed(1) + '%' : '-'}</td>
						<td>{samsungSECalc !== null ? samsungSECalc.toFixed(1) + '%' : '-'}</td>
					</tr>
					<tr>
						<td class="fw-medium">입면시각</td>
						<td>{sleepwaveOnsetTime ? formatTime(sleepwaveOnsetTime) : '-'}</td>
						<td>{samsungOnsetTime ? formatTime(samsungOnsetTime) : '-'}</td>
					</tr>
					<tr>
						<td class="fw-medium">입면 시간 (SL)</td>
						<td>{sleepwaveOnsetTime ? formatSeconds(ourData.sleepData?.sleepLatency) : '-'}</td>
						<td>{samsungSLSec !== null && samsungSLSec >= 0 ? formatSeconds(samsungSLSec) : '-'}</td>
					</tr>
					<tr>
						<td class="fw-medium">WASO</td>
						<td>{ourDurations ? formatSeconds(ourDurations.awake) : '-'}</td>
						<td>{samsungDurations ? formatSeconds(samsungDurations.awake) : '-'}</td>
					</tr>
					<tr>
						<td class="fw-medium">Deep</td>
						<td>{ourDurations ? formatSeconds(ourDurations.deep) : '-'}</td>
						<td>{samsungDurations ? formatSeconds(samsungDurations.deep) : '-'}</td>
					</tr>
					<tr>
						<td class="fw-medium">Light</td>
						<td>{ourDurations ? formatSeconds(ourDurations.light) : '-'}</td>
						<td>{samsungDurations ? formatSeconds(samsungDurations.light) : '-'}</td>
					</tr>
					<tr>
						<td class="fw-medium">REM</td>
						<td>{ourDurations ? formatSeconds(ourDurations.rem) : '-'}</td>
						<td>{samsungDurations ? formatSeconds(samsungDurations.rem) : '-'}</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</div>
